import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CashbookSidebar from "../CashbookSidebar";
import SwitchLayoutBanner from "../../../SwitchLayoutBanner";
import CashbookEmptyOverlay from "../CashbookEmptyOverlay";
import BusinessStatusBanner from "../../Business/BusinessStatusBanner";
import { isBusinessMainBlocked } from "../../../../Constants/business";
import {
  requestAddMainBusiness,
  requestListBusiness,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../../../Context";

const CashbookOverlaySkeleton = () => (
  <main className="lg:col-span-9">
    {/* Header skeleton */}
    <div className="px-4 py-5 sm:px-6 border-b border-gray-200 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="h-9 bg-gray-200 rounded w-36" />
      </div>
    </div>

    {/* Content skeleton */}
    <div className="relative min-h-[520px]">
      <div
        className="px-4 sm:px-6 py-6 select-none"
        aria-hidden="true"
        style={{ filter: "blur(3px)", opacity: 0.4, pointerEvents: "none" }}
      >
        <div className="animate-pulse">
          <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 flex gap-16">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-3 bg-gray-200 rounded w-16" />
              ))}
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="px-6 py-4 flex gap-16 border-t border-gray-200"
              >
                <div className="h-4 bg-gray-100 rounded w-20" />
                <div className="h-4 bg-gray-100 rounded w-40" />
                <div className="h-4 bg-gray-100 rounded w-16" />
                <div className="h-4 bg-gray-100 rounded w-24 ml-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Overlay card skeleton */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px]">
        <div className="mx-4 max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden animate-pulse">
            <div className="px-6 py-8 sm:px-10">
              <div className="flex justify-center mb-6">
                <div className="h-12 w-12 rounded-xl bg-gray-200" />
              </div>
              <div className="flex flex-col items-center mb-8">
                <div className="h-6 bg-gray-200 rounded w-48 mb-6" />
                <div className="h-4 bg-gray-200 rounded w-64 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-36" />
              </div>
              <div className="space-y-4 mb-8">
                <div className="h-4 bg-gray-200 rounded w-52 mx-auto mb-2" />
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="h-5 w-5 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded flex-1" />
                  </div>
                ))}
              </div>
              <div className="h-10 bg-gray-200 rounded-lg w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
);

const CashbookWrapper = ({ children }) => {
  const navigate = useNavigate();

  const { user } = useAuthState();
  const { businesses, loading: businessLoading } =
    useBusinessTransactionState();

  const authDispatch = useAuthDispatch();
  const businessDispatch = useBusinessTransactionDispatch();

  const [showCashbookBanner, setShowCashbookBanner] = useState(true);
  const [businessResolved, setBusinessResolved] = useState(
    !!user?.mainBusiness?.id,
  );
  const prevBusinessLoading = useRef(false);

  const hasNoBusiness = !user?.mainBusiness?.id && businesses.length === 0;

  useEffect(() => {
    if (prevBusinessLoading.current && !businessLoading) {
      setBusinessResolved(true);
    }
    prevBusinessLoading.current = businessLoading;
  }, [businessLoading]);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      setBusinessResolved(true);
    }
  }, [user?.mainBusiness?.id]);

  useEffect(() => {
    if (!user?.mainBusiness?.id && user?.id) {
      requestListBusiness(businessDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessDispatch, user?.id, user?.mainBusiness?.id]);

  // Prefer a healthy business when auto-promoting: a RESTRICTED /
  // PENDING_DELETION one would make a read-only workspace the account-wide
  // default. Fall back to the first business when every one is blocked, so the
  // page still has something to render.
  useEffect(() => {
    if (!user?.mainBusiness?.id && businesses.length > 0) {
      const candidate =
        businesses.find(
          (business) => business?.id && !isBusinessMainBlocked(business),
        ) || businesses[0];
      if (candidate?.id) {
        requestAddMainBusiness(authDispatch, {
          businessID: candidate.id,
          business: candidate,
        });
      }
    }
  }, [authDispatch, businesses, user?.mainBusiness?.id]);

  const handleSwitchToOldLayout = () => {
    localStorage.removeItem("cashbook_version");
    navigate("/cash-book");
  };

  const renderContent = () => {
    if (hasNoBusiness) {
      if (!businessResolved) {
        return <CashbookOverlaySkeleton />;
      }
      return <CashbookEmptyOverlay />;
    }

    const mainBusiness = user?.mainBusiness;
    const showRibbon =
      mainBusiness?.status && mainBusiness.status !== "ACTIVE";

    return (
      <div className="col-span-9">
        {showRibbon && (
          <div className="px-4 pt-4 sm:px-6">
            <BusinessStatusBanner business={mainBusiness} />
          </div>
        )}
        {children}
      </div>
    );
  };

  return (
    <div className="max-w-screen-xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
          <CashbookSidebar />
          {renderContent()}
        </div>
      </div>
      {showCashbookBanner && (
        <div className="mt-4">
          <SwitchLayoutBanner
            labelKey="switchLayoutLabels.oldCashbookLayout"
            onSwitch={handleSwitchToOldLayout}
            onDismiss={() => setShowCashbookBanner(false)}
            variant="light"
            direction="back"
          />
        </div>
      )}
    </div>
  );
};

export default CashbookWrapper;
